import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { EMPTY, expand, map, Observable, reduce } from 'rxjs';

import { APP_RUNTIME_CONFIG } from '@core/auth/app-config.model';

import {
  CareerPathwayDetailsDto,
  CareerPathwayDto,
  PersonaCareerPathwayDto,
  PersonaCareerPathwayPageDto,
  ReplaceCareerPathwayTiersDto,
} from './career-pathways.dto';

/**
 * Career Pathways — `/api/v1/career-pathways`.
 *
 * Reading is open to everybody (a placement is org-chart information, and the
 * placements read answers only the rows the caller may see). Every write needs
 * edit access to Career Pathways, which the server checks; the page offers the
 * edit icons only to the same people.
 *
 * **A ladder and its positions are one write**: `PUT …/tiers` carries both and
 * applies them in one transaction. The separate `PUT …/positions` is not used
 * here — the tier editor saves the two together.
 */
@Injectable({ providedIn: 'root' })
export class CareerPathwaysApiService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(APP_RUNTIME_CONFIG);

  /** Whether a backend is configured. Empty `apiUrl` = the seeded prototype. */
  available(): boolean {
    return !!this.config.apiUrl;
  }

  private url(path = ''): string {
    return `${this.config.apiUrl.replace(/\/$/, '')}/career-pathways${path}`;
  }

  /** Every pathway with its tiers, by name. */
  list(): Observable<CareerPathwayDto[]> {
    return this.http.get<CareerPathwayDto[]>(this.url());
  }

  /**
   * Every placement the caller may see, every page of it.
   *
   * The read is paged, and the page needs all of it at once — a person's tier is
   * drawn on the ladder, and a placement on page two is somebody missing from it.
   * Asks for the server's largest page and follows `totalPages` from there.
   */
  allPlacements(): Observable<PersonaCareerPathwayDto[]> {
    const page = (n: number) =>
      this.http.get<PersonaCareerPathwayPageDto>(this.url('/placements'), {
        params: new HttpParams().set('page', n).set('size', 200),
      });
    return page(0).pipe(
      expand((p) => (p.page + 1 < p.totalPages ? page(p.page + 1) : EMPTY)),
      map((p) => p.content),
      reduce((all, rows) => [...all, ...rows], [] as PersonaCareerPathwayDto[]),
    );
  }

  /** A new pathway, with no tiers yet. 409 when the name is already taken. */
  create(body: CareerPathwayDetailsDto): Observable<CareerPathwayDto> {
    return this.http.post<CareerPathwayDto>(this.url(), body);
  }

  /** A pathway's own fields: name, division, description. */
  update(guid: string, body: CareerPathwayDetailsDto): Observable<CareerPathwayDto> {
    return this.http.patch<CareerPathwayDto>(this.url(`/${guid}`), body);
  }

  /**
   * The whole ladder, replaced in one call and answered whole. Refused (409)
   * when it drops a tier somebody stands on.
   */
  replaceTiers(guid: string, body: ReplaceCareerPathwayTiersDto): Observable<CareerPathwayDto> {
    return this.http.put<CareerPathwayDto>(this.url(`/${guid}/tiers`), body);
  }
}
